import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { apiError, safeNext } from "@/lib/api";

export default function Login() {
  const { login } = useAuth();
  const nav = useNavigate();
  const [params] = useSearchParams();
  const next = safeNext(params.get("next"));
  const fromCheckout = next.startsWith("/payment/success");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await login(email.trim(), password);
      toast.success("Welcome back");
      nav(next);
    } catch (err) {
      toast.error(apiError(err, "Login failed"));
    }
    setBusy(false);
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-6 grain">
      <div className="w-full max-w-md apx-glass rounded-2xl p-8 rise" data-testid="login-card">
        <div className="flex flex-col items-center mb-8">
          <img
            data-testid="brand-hero-logo"
            src="/brand/evidex-logo-notag.jpg"
            alt="EV-IDEX"
            className="w-full max-w-[340px] h-auto rounded-xl"
          />
          <div className="mt-3 text-[10px] font-mono-ticker uppercase tracking-widest text-slate-500">
            Crypto Research · Paper Trading
          </div>
        </div>

        <h1 className="font-display text-3xl font-bold mb-1">Welcome back</h1>
        <p className="text-sm text-slate-400 mb-6">Sign in to continue your research.</p>
        {fromCheckout && (
          <div className="mb-5 rounded-lg border border-emerald-900/60 bg-emerald-500/10 p-3 text-xs text-emerald-200" data-testid="login-checkout-banner">
            Payment received — sign in to finish activating your plan. Your subscription is already being confirmed with Stripe.
          </div>
        )}

        <form onSubmit={submit} className="flex flex-col gap-4">
          <label className="flex flex-col gap-1.5">
            <span className="text-xs font-mono-ticker uppercase tracking-widest text-slate-500">Email</span>
            <input
              data-testid="login-email"
              required type="email" value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="apx-ring px-3 py-2.5 rounded-lg bg-slate-900/80 border border-slate-800 outline-none"
              placeholder="trader@ev-idex.com"
            />
          </label>
          <label className="flex flex-col gap-1.5">
            <span className="text-xs font-mono-ticker uppercase tracking-widest text-slate-500">Password</span>
            <input
              data-testid="login-password"
              required type="password" minLength={6} value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="apx-ring px-3 py-2.5 rounded-lg bg-slate-900/80 border border-slate-800 outline-none"
              placeholder="••••••••"
            />
          </label>
          <button
            data-testid="login-submit"
            disabled={busy}
            className="btn-primary rounded-lg py-2.5 font-medium flex items-center justify-center gap-2 disabled:opacity-60"
          >
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
            Sign in
          </button>
        </form>

        <div className="mt-6 text-sm text-slate-400">
          New here?{" "}
          <Link data-testid="go-signup" to="/signup" className="text-cyan-300 hover:underline">Create an account</Link>
        </div>
        <div className="mt-6 text-[10px] font-mono-ticker uppercase tracking-widest text-amber-300/80">
          Paper trading only · Not financial advice
        </div>
        <div className="mt-6 text-[11px] text-slate-500 leading-relaxed" data-testid="auth-legal">
          By continuing you agree to the <Link to="/terms" className="text-slate-300 hover:text-cyan-200 underline">Terms of Service</Link>, <Link to="/privacy" className="text-slate-300 hover:text-cyan-200 underline">Privacy Policy</Link> and <Link to="/risk-disclosure" className="text-slate-300 hover:text-cyan-200 underline">Risk Disclosure</Link>. EV-IDEX is a research and paper-trading platform — not a broker, exchange or adviser, and never a guarantee of returns.
        </div>
        <div className="mt-3 text-[11px] text-slate-500 flex flex-wrap gap-x-3"><Link to="/" className="hover:text-cyan-200">About EV-IDEX</Link><Link to="/contact" className="hover:text-cyan-200">Contact & Support</Link><Link to="/partners" className="hover:text-cyan-200">Investment Partners</Link></div>
      </div>
    </div>
  );
}
